
import React, { useState, useEffect, useMemo } from 'react';
import { ALL_FEATURES } from './features/index.ts';
import type { ViewType } from '../types.ts';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (view: ViewType) => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSelectedIndex(0);
    }
  }, [isOpen]);
  
  const commandOptions = useMemo(() => {
    const navigationCommands = [
      { id: 'ai-feature-builder', name: 'Go to AI Builder', category: 'Navigation', icon: <span />, description: ''},
      { id: 'home', name: 'Go to Machine', category: 'Navigation', icon: <span />, description: ''},
      { id: 'features-list', name: 'Go to Features List', category: 'Navigation', icon: <span />, description: ''},
      { id: 'ai-code-explainer', name: 'Open AI Code Explainer', category: 'Navigation', icon: <span />, description: ''},
    ];
    
    const featureCommands = ALL_FEATURES.map(f => ({...f, name: `Open: ${f.name}`}));

     return [
      ...navigationCommands,
      ...featureCommands,
     ].filter(
        (feature) =>
          feature.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          feature.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
  }, [searchTerm]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [commandOptions.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % commandOptions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + commandOptions.length) % commandOptions.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selected = commandOptions[selectedIndex];
        if (selected) {
          onSelect(selected.id as ViewType);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, commandOptions, selectedIndex, onSelect]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-start justify-center pt-20" onClick={onClose}>
      <div
        className="w-full max-w-xl bg-slate-800 border border-slate-700 rounded-lg shadow-2xl shadow-black/50 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="text"
          placeholder="Type a command or search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
          className="w-full p-4 bg-slate-800 text-slate-200 text-lg focus:outline-none border-b border-slate-700"
        />
        <ul className="max-h-96 overflow-y-auto p-2">
          {commandOptions.length > 0 ? (
            commandOptions.map((item, index) => (
              <li
                key={item.id + index}
                onMouseDown={() => {
                   onSelect(item.id as ViewType);
                }}
                className={`flex items-center justify-between p-3 rounded-md cursor-pointer ${
                  selectedIndex === index ? 'bg-cyan-500/20' : 'hover:bg-slate-700/50'
                }`}
              >
                <div className="flex items-center space-x-3">
                    <div className="text-slate-500">{item.icon}</div>
                    <span className="text-slate-200">{item.name}</span>
                </div>
                <span className="text-xs text-slate-500 bg-slate-700 px-2 py-1 rounded">{item.category}</span>
              </li>
            ))
          ) : (
            <li className="p-4 text-center text-slate-500">No results found.</li>
          )}
        </ul>
      </div>
    </div>
  );
};